import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ConnectorGroup } from 'models/ConnectorGroup';
import { Connector } from 'models/Connector';

type EntityState = {
  connectorGroupsById: Record<number, ConnectorGroup>;
  connectorsById: Record<number, Connector>;
  connectorsLoaded: boolean;
};

type EntityContextValue = EntityState & {
  upsertConnectorGroups: (connectorGroups: ConnectorGroup[]) => void;
  upsertConnectors: (connectors: Connector[]) => void;
  clearEntities: () => void;
};

const EntityContext = createContext<EntityContextValue | null>(null);

export const EntityProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [connectorGroupsById, setConnectorGroupsById] = useState<Record<number, ConnectorGroup>>({});
  const [connectorsById, setConnectorsById] = useState<Record<number, Connector>>({});
  const [connectorsLoaded, setConnectorsLoaded] = useState(false);

  const upsertConnectorGroups = useCallback((connectorGroups: ConnectorGroup[]) => {
    if (!connectorGroups || connectorGroups.length === 0) return;
    setConnectorGroupsById(prev => {
      const next = { ...prev };
      connectorGroups.forEach(connectorGroup => {
        next[connectorGroup.id] = connectorGroup;
      });
      return next;
    });
  }, []);

  const upsertConnectors = useCallback((connectors: Connector[]) => {
    setConnectorsLoaded(true);
    if (!connectors || connectors.length === 0) return;
    setConnectorsById(prev => {
      const next = { ...prev };
      connectors.forEach(connector => {
        next[connector.id] = connector;
      });
      return next;
    });
  }, []);

  const clearEntities = useCallback(() => {
    setConnectorGroupsById({});
    setConnectorsById({});
    setConnectorsLoaded(false);
  }, []);

  const value = useMemo(
    () => ({
      connectorGroupsById,
      connectorsById,
      connectorsLoaded,
      upsertConnectorGroups,
      upsertConnectors,
      clearEntities
    }),
    [connectorGroupsById, connectorsById, connectorsLoaded, upsertConnectorGroups, upsertConnectors, clearEntities]
  );

  return <EntityContext.Provider value={value}>{children}</EntityContext.Provider>;
};

export const useEntities = () => {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error('useEntities must be used within an EntityProvider');
  }
  return context;
};
